import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import {
  useCreateRecipeSpecificGenericProcess,
  useGetRecipeSpecificGenericProcess
} from "../../../api/recipe-specific-generic-process";
import { useGetStaticRecipeProcessTypes } from "../../../api/static-data";
import { successMessage } from "../../../helpers/ErrorHandler";
import { CompanyRow } from "../companyList/types/companyListView.types";
import AllowListModal from "../shared/components/AllowListModal";

interface GenericProcessModalProps {
  company: CompanyRow | null;
  closeModal: () => void;
}

/**
 * Tailwind rebuild of the legacy `GenericProcessModel`
 * (src/screens/company/genericProcessModal/GenericProcessModel.tsx): same
 * queries/mutation, same case-insensitive name search, same success message
 * — and, as before, an empty selection is allowed (clears the company's
 * generic processes). Open whenever `company` is set.
 */
const GenericProcessModal = ({ company, closeModal }: GenericProcessModalProps) => {
  const [search, setSearch] = useState("");
  const [checked, setChecked] = useState<Array<string | number>>([]);

  const { data: processData } = useGetRecipeSpecificGenericProcess(company?.tenantId as string);
  const { data: staticProcessTypes } = useGetStaticRecipeProcessTypes();
  const { isLoading: isSaving, mutate: createGenericProcess } =
    useCreateRecipeSpecificGenericProcess();

  useEffect(() => {
    setChecked(processData?.map((item: any) => item?.processId) || []);
  }, [processData]);

  const items = useMemo(() => {
    const re = new RegExp(_.escapeRegExp(search), "i");
    return (staticProcessTypes || [])
      .filter((res: any) => re.test(res?.text))
      .map((item: any) => ({
        value: item.value,
        text: item.text,
        createdAt: processData?.find((process: any) => process.processId === item.value)?.createdAt
      }));
  }, [search, staticProcessTypes, processData]);

  const toggle = (value: string | number) =>
    setChecked(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));

  const confirm = () => {
    createGenericProcess(
      { processIds: checked, tenantId: company?.tenantId },
      {
        onSuccess: () => {
          closeModal();
          successMessage("Recipe specific process add and remove successfully");
        }
      }
    );
  };

  return (
    <AllowListModal
      visibleModal={!!company}
      title={`Add Generic Process for ${company?.name ?? ""}`}
      nameColumnLabel="Process Name"
      searchPlaceholder="Search processes"
      items={items}
      checkedValues={checked}
      onToggle={toggle}
      searchValue={search}
      onSearchChange={setSearch}
      isSaving={isSaving}
      onConfirm={confirm}
      onClose={closeModal}
    />
  );
};

export default GenericProcessModal;
